import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as applicationActions from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.actions";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import * as propertyAmenityHooks from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.hooks";
import { getAmenityNumberValue } from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.utils";
import {
  EIntakeStep,
  IApplication,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { calculateRentMatchScore } from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import {
  Checkbox,
  DropdownMenu,
  Icon,
  MultiStepForm,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import React, { useContext } from "react";
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from "react-beautiful-dnd";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { StringParam } from "serialize-query-params";
import {
  getPhaseForStep,
  isCandidacySubmission,
  shouldShowIncome,
} from "utils/application.utils";
import { Rating } from "../../../../../../components/Rating";
import createDuplicateApplicationForm from "../../../../../../forms/duplicateApplication";
import * as t from "../../../../../../services/translationService";
import { DragAndDropContext } from "../../context/DragAndDropContext";
import { IApplicationDropDownHandler } from "../../PropertyDetailApplications";
import {
  ERentMatchBand,
  getRentMatchBand,
  getRentMatchBandLabelKey,
} from "../../rentmatch.bands";
import { RM_COLORS } from "../../rentmatch.colors";
import { BARE_TOOLTIP_TRIGGER_STYLE } from "../../tooltipTrigger.styles";
import {
  EPdfStatus,
  getContactActions,
  getViewActions,
} from "../applicationActions.utils";
import {
  getLanguage,
  getResidentsCount,
  getScoreAvailableDate,
  getScoreMoveInDate,
  getTotalIncome,
} from "../DragItem/dragItem.utils";
import { OtherApplicationsModal } from "../OtherApplicationsModal";
import { WarningTooltip } from "../WarningTooltip";
import { ApplicationInfoIndicators } from "./applicationIndicators";
import * as S from "./ApplicationTable.styled";

interface IProps {
  item: IApplication;
  step: number;
  index: number;
  allowedSteps: number[];
  isDisabled?: boolean;
  isSelected: boolean;
  handler: IApplicationDropDownHandler;
  renderAsDragItem?: boolean;
  applicationVisit?: any;
  isLast?: boolean;
}

const formId = generateFormId();

export const ApplicationTableRow = React.memo((props: IProps) => {
  const {
    item,
    step,
    handler,
    index,
    isDisabled: _isDisabled,
    isSelected: _isSelected,
    renderAsDragItem = true,
    applicationVisit,
    isLast = false,
  } = props;

  const [, setQueryParamValue] = useQueryParams({
    applicationId: StringParam,
  });

  const [clickX, setClickX] = React.useState(0);

  const dispatch = useDispatch();
  const { broker } = authHooks.useSelf();

  const intakeType = broker?.intakeType || 2;
  const hasCalendarSync = useBrokerFeature(EBrokerFeature.CalendarSync);

  const {
    onSelectItem,
    onUnselectItem,
    replaceSelectedItems,
    selectedItems,
    isGlobalDragging,
  } = useContext(DragAndDropContext);

  const params = useParams<{ propertyId: string }>();
  const propertyId = params.propertyId ? +params.propertyId : undefined;

  applicationHooks.useArchive({});
  applicationHooks.useUnarchive({});

  const { detail: property } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: propertyId,
  });

  const { items: propertyAmenities } = propertyAmenityHooks.useGetByPropertyId({
    propertyId,
  });

  const selectedItemIds = selectedItems.map(si => si.id) || [];

  const [showDuplicateModal, setShowDuplicateModal] =
    React.useState<boolean>(false);
  const [showOtherAppsModal, setShowOtherAppsModal] =
    React.useState<boolean>(false);
  const [pdfStatus, setPdfStatus] = React.useState(EPdfStatus.Checking);
  const { contacts, residents } = item;

  const totalIncome = getTotalIncome(contacts);
  const adultCount = getResidentsCount(residents, "INTAKE_RESIDENT_ADULTS");
  const childrenCount = getResidentsCount(
    residents,
    "INTAKE_RESIDENT_CHILDREN",
  );

  // Delivered is still read off the document arrays, which are truthful. What
  // is no longer derived here is how many were OWED: that is a property of the
  // phase the submission was made in, so only the sender can state it, and it
  // now arrives on the row (DEV-3135). Deriving it from the number of people
  // and income sources is what produced a 0/2 for a visit request nobody had
  // asked for a document, and then a per-phase gate on top to hide that again.
  //
  // The Brussels exemption sits inside that number too, frozen at submission
  // rather than re-derived from the property zip at render time. So on a
  // Brussels property the count can be 0 while an identity document does exist
  // in the array: nothing was owed, so no fraction is shown. The document
  // itself is still listed in the detail panel, which counts nothing.
  //
  // "I cannot manage this digitally, I will bring these documents in myself"
  // (handOff) counts as required and not delivered, so such a candidate reads
  // 0/1. Decided by Jonas on 2026-09-01, and the reasoning is the plain one:
  // these numbers say what the broker holds, and the broker does not hold this
  // document. An announcement is not a document. Legacy empties the document
  // list for a hand-off (application-profile.js, `if (contact.handOff ||
  // !isCandidacy) contact.documents = []`), which is why nothing arrives to
  // count, and the PDF reports the same document as missing in its finance
  // section. So the fraction, the paper and the truth already agree; do not
  // "fix" this into counting hand-offs apart.
  // Zero income means the question was never put, so the cell stays empty
  // rather than showing a value nobody gave. The household follows the same
  // rule inside the shared indicator block.
  const showIncome = shouldShowIncome(totalIncome);

  // The pill, and the check inside it, say one thing: this row is a candidacy
  // that was really submitted. Read from the step the submission was made with,
  // which does not move when a broker drags the candidate, so it survives a
  // later move to tenant. It used to hang on a timestamp that reset on every
  // drag (DEV-3135).
  //
  // Only on the candidate-tenant tab. Elsewhere the phase name is the more
  // useful label, and the archive has no label at all, so with the check gone
  // the pill does not render there rather than rendering empty.
  const showsSubmittedPill =
    step === EIntakeStep.candidateTenant &&
    isCandidacySubmission(item.submittedStep);

  const handleDuplicate = ({ step, propertyId: newPropertyId }: any) => {
    if (!newPropertyId) {
      return;
    }

    dispatch(
      applicationActions.duplicateApplicationStart.getAction({
        id: item.id,
        propertyId: newPropertyId,
        step,
        onSuccess(result) {
          handler?.([result.id], step, result);
        },
      }),
    );

    setShowDuplicateModal(false);
  };

  const handleCloseModal = () => {
    setShowDuplicateModal(false);
  };

  const { archive } = applicationHooks.useArchive({});
  const { unarchive } = applicationHooks.useUnarchive({});

  const checkAvailability = async () => {
    if (!item?.pdfUrl) {
      setPdfStatus(EPdfStatus.NotAvailable);
      return;
    }
    try {
      // Use a ranged GET (not HEAD): the signed S3 URLs reject/ignore HEAD,
      // which left the status stuck on "generating" so the CV could never be
      // opened. The Range header fetches only the first byte instead of the
      // whole PDF just to check existence (S3 answers 206 on success).
      const response = await fetch(item.pdfUrl as URL | RequestInfo, {
        method: "GET",
        headers: { Range: "bytes=0-1" },
      });
      const isAvailable = response.status === 200 || response.status === 206;
      setPdfStatus(isAvailable ? EPdfStatus.Generated : EPdfStatus.Generating);
    } catch {
      setPdfStatus(EPdfStatus.NotAvailable);
    }
  };

  const getRowActions = () => {
    const duplicateActions = [
      {
        content: t.applicationDuplicateApplicationAction(),
        onClick: () => setShowDuplicateModal(true),
      },
    ];

    const contactRowActions = getContactActions({ item });

    const viewActions = getViewActions({
      item,
      pdfStatus,
      setQueryParamValue,
      isDetailView: false,
    });

    const archiveActions = [
      {
        content: item.archivedAt
          ? getLocalizedText("applications.cta.unarchive")
          : getLocalizedText("applications.cta.archive"),
        onClick: () => {
          if (item.archivedAt) {
            unarchive({}, item.id);
          } else {
            archive({}, item.id);
          }
        },
      },
    ];

    return [duplicateActions, contactRowActions, viewActions, archiveActions];
  };

  if (!propertyId) {
    return null;
  }

  const handleSelectNewItem = (item: IApplication) => {
    const isFirstSelection = selectedItems.length === 0;
    if (isFirstSelection) {
      onSelectItem(item);
      return;
    }

    // In the archive the rows are rendered with step === 0, which has no phase
    // (getPhaseForStep returns -1). Archived candidates are not phase-grouped,
    // so allow any combination to be multi-selected together instead of
    // replacing the previous selection on every click.
    if (step === 0) {
      onSelectItem(item);
      return;
    }

    const phaseForOtherItems = getPhaseForStep(
      intakeType,
      selectedItems[0].step,
    );
    const phaseForNewItem = getPhaseForStep(intakeType, step);

    if (phaseForOtherItems === phaseForNewItem) {
      onSelectItem(item);
    } else {
      replaceSelectedItems([item]);
    }
  };

  const handleSelectItem = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedItemIds.includes(item.id)) {
      handleSelectNewItem(item);
    } else {
      onUnselectItem(item);
    }
  };

  const renderRowContent = () => {
    // Determine the primary contact and application counts
    const primaryContact = contacts[0] || {};

    // All tenants on the application, with the language only behind the main
    // applicant (the first contact). The language of a co-tenant is NOT
    // unknown, it is wrong: legacy copies the main applicant's locale onto
    // every contact row, so printing it would present someone else's language
    // as theirs. It therefore has to be suppressed for the others rather than
    // rendered whenever the field happens to be filled.
    const primaryName = getName(primaryContact);
    const primaryLanguage = primaryContact.language
      ? getLanguage(primaryContact.language)
      : undefined;
    const coTenantNames = contacts
      .slice(1)
      .map((contact: any) => getName(contact))
      .join(", ");
    // Plain-text version for the hover title, which cannot carry markup.
    const candidateNames = [
      primaryLanguage ? `${primaryName} (${primaryLanguage})` : primaryName,
      coTenantNames,
    ]
      .filter(Boolean)
      .join(", ");
    const propertyCount = (item as any).otherApplicationsTotalCount || 0;

    // Calculate rent ratio
    const rentPrice = (property as any)?.rentPrice || 0;
    const rentRatio = rentPrice > 0 ? totalIncome / rentPrice : 0;
    const formattedRatio =
      rentRatio > 0
        ? `(${rentRatio.toLocaleString("nl-BE", { maximumFractionDigits: 1 })})`
        : "";

    const hasNoShows = !!item.otherApplicationsMeta?.noShows?.length;
    const hasNotes = !!item.otherApplicationsMeta?.notes?.length;
    const hasCurrentNoShow = !!item.noShowAt;
    const internalNoteContent =
      (item.otherApplicationsMeta as any)?.internalNote ||
      (primaryContact as any).note;
    const hasInternalNote = !!internalNoteContent;
    const hasCrossPropertyWarnings =
      hasNoShows || hasNotes || hasCurrentNoShow || hasInternalNote;

    // Step badge label (current step) — skip for archived (step=0)
    let stepLabel =
      step > 0
        ? t.applicationStateTitleRentmatch(step as unknown as EIntakeStep)
        : "";

    // Distinguish a candidacy that landed on the waiting list automatically from
    // one a broker dragged there manually.
    if (step === EIntakeStep.waitingList && item.autoWaitlisted) {
      stepLabel = getLocalizedText(
        "property.dashboard.application.7.title.automatic",
      );
    }

    if (
      step === EIntakeStep.visitor &&
      (applicationVisit?.start || applicationVisit?.slotStart)
    ) {
      stepLabel = t.applicationStateVisitScheduled();
    }

    // The date beside this label is the submission moment, so where there is no
    // phase name worth keeping the label says so rather than repeating the
    // column name (DEV-3107). A candidate a broker dragged in has no
    // completedAt, keeps the phase name, and its date is when the broker moved
    // them.
    if (showsSubmittedPill) {
      stepLabel = getLocalizedText("rentmatch.applications.state.submitted");
    }

    // RentMatch Calculation
    const matchBasePrice = property?.price || 0;
    const matchCosts = (property as any)?.costs || 0;

    const propertyDetails = {
      rent: matchBasePrice + matchCosts,
      bedrooms: propertyAmenities
        ? getAmenityNumberValue(
            propertyAmenities,
            "rentio.default.room_count_bedroom",
          ) || null
        : null,
      availableDate: getScoreAvailableDate(property),
      hasBalcony: false,
      hasGarden: false,
      floorType: "other" as const,
    };

    const userDetails = {
      netIncome: totalIncome,
      otherIncome: 0,
      adults: adultCount,
      minors: childrenCount,
      ...getScoreMoveInDate(item.dateOfOccupancy),
    };

    const rentMatch =
      propertyDetails.rent > 0
        ? calculateRentMatchScore(userDetails, propertyDetails)
        : null;

    // Red marks the number that kept this candidate below a perfect match, so
    // a broker sees at a glance which part is the problem instead of reading
    // the percentage and guessing (DEV-3075). Anything below 1.0 counts: the
    // rule is "not perfect", not "below the minimum".
    //
    // Adults and children go red together because the score has one household
    // figure, not one per group: it works out how many bedrooms this household
    // needs and compares that with the property, so neither number is the
    // culprit on its own.
    //
    // That figure also drops when the property is LARGER than the household
    // needs, which reads as the opposite of overcrowding. Red anyway, decided
    // by Jonas on 2026-09-03: the point is to show which category cost the
    // score, and that one did.
    //
    // Neither turns red without a score. A property with no rent produces no
    // score at all, and a guess would be worse than staying gray. The document
    // counter is the exception: it is not part of the score, so it reddens on
    // its own terms.
    //
    // Gated on the score the row actually shows, not only on the category:
    // the badge is rounded, so a category at 0.99 can still add up to a
    // displayed 100. Jonas's rule is that at a perfect score nothing is red, so
    // 100 percent beside a red number would contradict it.
    //
    // The move-in date is the third thing the score weighs and it has no column
    // here, so a row can show under 100 with nothing marked. Left that way on
    // purpose; a date column is its own ticket.
    const isPerfectMatch = !!rentMatch && rentMatch.score >= 100;
    const incomeBelowPerfect =
      !!rentMatch && !isPerfectMatch && rentMatch.incomePct < 1;
    const householdBelowPerfect =
      !!rentMatch && !isPerfectMatch && rentMatch.householdPct < 1;

    // Band and label come from rentmatch.bands.ts, the one definition shared
    // with the detail panel. The colors stay here: this cell paints a pill, so
    // it needs a soft background with dark text where the panel needs solid
    // ring colors for the same band.
    const getRentBgColor = (score: number) => {
      switch (getRentMatchBand(score)) {
        case ERentMatchBand.Perfect:
        case ERentMatchBand.High:
          return RM_COLORS.greenBgSoft;
        case ERentMatchBand.Average:
          return RM_COLORS.amberBg;
        default:
          return RM_COLORS.redBg;
      }
    };

    const getRentTextColor = (score: number) => {
      switch (getRentMatchBand(score)) {
        case ERentMatchBand.Perfect:
        case ERentMatchBand.High:
          return RM_COLORS.greenDarker;
        case ERentMatchBand.Average:
          return RM_COLORS.amberDark;
        default:
          return RM_COLORS.redDark;
      }
    };

    return (
      <>
        <S.Cell
          onClick={(e: React.MouseEvent) => {
            // Keep a checkbox click from bubbling to the row's onClick (which
            // opens the detail drawer). In the draggable tabs react-beautiful-dnd
            // already swallows this click, but the archive rows are not
            // draggable, so without this the drawer opened and the selection was
            // lost — breaking independent multi-select in the archive.
            e.stopPropagation();
          }}
        >
          <Checkbox
            onChangeValue={handleSelectItem}
            checked={selectedItemIds.includes(item.id)}
            appearance={
              selectedItemIds.includes(item.id) ? "success" : "default"
            }
            error={false}
          />
        </S.Cell>

        {/* Candidate / Contact */}
        <S.CandidateNameContainer>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <S.CandidateName title={candidateNames}>
              {primaryName}
              {primaryLanguage && (
                <S.CandidateLanguage>({primaryLanguage})</S.CandidateLanguage>
              )}
              {coTenantNames ? `, ${coTenantNames}` : null}
            </S.CandidateName>
            {/* Warning triangle — visible when cross-property warnings OR internal notes exist */}
            {hasCrossPropertyWarnings && (
              <WarningTooltip
                size={18}
                tooltipContent={
                  <div style={{ maxWidth: "240px" }}>
                    {hasCurrentNoShow && (
                      <div>
                        <strong>
                          {getLocalizedText(
                            "application.cross_property.tooltip.noshow",
                          )}
                        </strong>
                        <br />
                        {formatDate(new Date(item.noShowAt!), "d MMMM yyyy")}
                        <br />
                        {property ? formatAddress(property as any) : ""}
                      </div>
                    )}
                    {hasNoShows &&
                      item.otherApplicationsMeta!.noShows.map((ns, idx) => (
                        <React.Fragment key={`ns-${idx}`}>
                          {(hasCurrentNoShow || idx > 0) && (
                            <hr
                              style={{
                                border: "none",
                                borderTop: `1px solid ${RM_COLORS.amberBorder}`,
                                margin: "5px 0",
                              }}
                            />
                          )}
                          <div>
                            <strong>
                              {getLocalizedText(
                                "application.cross_property.tooltip.noshow",
                              )}
                            </strong>
                            <br />
                            {formatDate(new Date(ns.noShowAt), "d MMMM yyyy")}
                            <br />
                            {ns.propertyAddress}
                          </div>
                        </React.Fragment>
                      ))}
                    {hasInternalNote && (
                      <>
                        {(hasCurrentNoShow || hasNoShows) && (
                          <hr
                            style={{
                              border: "none",
                              borderTop: `1px solid ${RM_COLORS.amberBorder}`,
                              margin: "5px 0",
                            }}
                          />
                        )}
                        <div
                          style={{ fontStyle: "italic", marginBottom: "6px" }}
                        >
                          "{internalNoteContent}"
                        </div>
                      </>
                    )}
                    {hasNotes &&
                      item.otherApplicationsMeta!.notes.map((n, idx) => (
                        <React.Fragment key={`note-${idx}`}>
                          {(hasCurrentNoShow ||
                            hasNoShows ||
                            hasInternalNote ||
                            idx > 0) && (
                            <hr
                              style={{
                                border: "none",
                                borderTop: `1px solid ${RM_COLORS.amberBorder}`,
                                margin: "5px 0",
                              }}
                            />
                          )}
                          <div style={{ fontStyle: "italic" }}>
                            "{n.content}"<br />
                            {n.propertyAddress}
                          </div>
                        </React.Fragment>
                      ))}
                  </div>
                }
              />
            )}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              marginTop: "2px",
            }}
          >
            <Icon source="keys" size="extraSmall" />
            {propertyCount > 1 ? (
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "11px",
                }}
              >
                <span
                  role="button"
                  tabIndex={0}
                  style={{
                    fontSize: "11px",
                    color: RM_COLORS.blueStrong,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={() => setShowOtherAppsModal(true)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      setShowOtherAppsModal(true);
                    }
                  }}
                >
                  {getLocalizedText(
                    "application.cross_property.tooltip.multiple_applications",
                    { count: propertyCount },
                  )}
                </span>
              </div>
            ) : (
              <span style={{ fontSize: "11px", color: RM_COLORS.gray400 }}>
                {getLocalizedText(
                  "application.cross_property.tooltip.no_other_applications",
                )}
              </span>
            )}
          </div>
        </S.CandidateNameContainer>

        {/* RentMatch Score */}
        <S.Cell>
          {rentMatch ? (
            // Hovering the score names the band and what the score is based
            // on. Nothing else: the badge used to carry a native title with
            // the score warnings, which produced a second, bare browser
            // tooltip AND put tenant-facing advice in a broker's list (one of
            // them recommends the reader look for a property with fewer
            // bedrooms). Those warnings cannot be filtered here either, since
            // a warning carries only a color type and an already translated
            // sentence, so the tenant tip is indistinguishable from the
            // neutral ones. Broker-phrased reasons would need their own keys.
            <Tooltip
              style={BARE_TOOLTIP_TRIGGER_STYLE}
              placement="bottom"
              tooltipContent={
                <div style={{ maxWidth: "260px" }}>
                  <strong style={{ textTransform: "uppercase" }}>
                    {getLocalizedText(
                      getRentMatchBandLabelKey(rentMatch.score),
                    )}
                  </strong>
                  <div>
                    {getLocalizedText("rentmatch.applications.score.based_on")}
                  </div>
                </div>
              }
            >
              <S.RentMatchBadge
                bgColor={getRentBgColor(rentMatch.score)}
                textColor={getRentTextColor(rentMatch.score)}
              >
                {rentMatch.score}%
              </S.RentMatchBadge>
            </Tooltip>
          ) : (
            // There is exactly one reason for a missing score: the property has
            // no rent (price plus costs is 0). Rentmatch itself now skips the
            // score in that case too, so the dash shows up more often and needs
            // to explain itself.
            <Tooltip
              style={BARE_TOOLTIP_TRIGGER_STYLE}
              tooltipContent={getLocalizedText(
                "rentmatch.applications.score.no_rent_tooltip",
              )}
              placement="bottom"
            >
              <S.RentMatchBadge>-</S.RentMatchBadge>
            </Tooltip>
          )}
        </S.Cell>

        {/* Activity */}
        <S.Cell>
          <S.CandidateNameContainer>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ fontSize: "13px", color: RM_COLORS.gray700 }}>
                {step === EIntakeStep.visitor &&
                (applicationVisit?.start || applicationVisit?.slotStart)
                  ? utils.date.format(
                      new Date(
                        applicationVisit.start || applicationVisit.slotStart,
                      ),
                      "dd/MM/yyyy HH:mm",
                    )
                  : utils.date.format(
                      item.stepCompletedAt || item.stepStartedAt,
                      "dd/MM/yyyy HH:mm",
                    )}
              </span>
            </div>
            {(stepLabel || showsSubmittedPill) && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "3px",
                  marginTop: "3px",
                  padding: "2px 8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: RM_COLORS.greenStrong,
                  backgroundColor: RM_COLORS.greenBgAlt,
                  borderRadius: "9999px",
                  width: "fit-content",
                }}
              >
                {/* The check sits inside the label rather than beside the
                    date (DEV-3107): it marks a completed phase, so it belongs
                    with the words that name the phase. On the tenant and
                    not-eligible tabs that is the phase name, on candidate
                    tenant and the archive it is the submitted-application
                    wording.
                    currentColor keeps it on the label's own green, and it is
                    drawn heavier than the row's other icons because at this
                    size a thin stroke reads as a smudge. */}
                {showsSubmittedPill && (
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0 }}
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {stepLabel}
              </span>
            )}
          </S.CandidateNameContainer>
        </S.Cell>

        {/* Income */}
        <S.Cell>
          {/* A total of zero means income was never asked for, which happens in
              the visitor phase at a broker with optional income. Rendering it
              would show a candidate as having none, and the red marking would
              hold it against them, so the cell stays empty instead. */}
          {showIncome && (
            <>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: incomeBelowPerfect ? RM_COLORS.red : RM_COLORS.gray900,
                }}
              >
                {formatCurrency(totalIncome)}
              </span>
              {formattedRatio && (
                <span
                  style={{
                    fontSize: "13px",
                    color: RM_COLORS.gray500,
                    marginLeft: "4px",
                  }}
                >
                  {formattedRatio}
                </span>
              )}
            </>
          )}
        </S.Cell>

        {/* More info */}
        <S.Cell>
          {/* One component with the candidate list and the other-applications
              dialog, so the two cannot show different facts about the same
              record (DEV-3122). */}
          <ApplicationInfoIndicators
            application={item}
            householdBelowPerfect={householdBelowPerfect}
          />
        </S.Cell>

        {/* Rating */}
        <S.Cell>
          <div onClick={e => e.stopPropagation()}>
            <Rating
              value={item.rating || 0}
              size={14}
              onChange={value => {
                dispatch(
                  applicationActions.setApplicationRatingStart.getAction({
                    id: item.id,
                    value,
                  }),
                );
              }}
            />
          </div>
        </S.Cell>

        {/* Actions */}
        <S.ActionsCell>
          {(() => {
            // Define action buttons per step
            type StepAction = {
              label: string;
              targetStep?: EIntakeStep;
              icon: "positive" | "negative" | "hourglass";
              customAction?: () => void;
            };
            const actions: StepAction[] = [];

            switch (step) {
              case EIntakeStep.candidateVisitor:
                actions.push(
                  hasCalendarSync
                    ? {
                        label: getLocalizedText("application.action.invite"),
                        targetStep: EIntakeStep.invited,
                        icon: "positive",
                      }
                    : {
                        label: getLocalizedText("application.action.visit"),
                        targetStep: EIntakeStep.visitor,
                        icon: "positive",
                      },
                );
                actions.push({
                  label: getLocalizedText("application.action.waitlist"),
                  targetStep: EIntakeStep.waitingList,
                  icon: "hourglass",
                });
                break;

              case EIntakeStep.waitingList:
                actions.push(
                  hasCalendarSync
                    ? {
                        label: getLocalizedText("application.action.invite"),
                        targetStep: EIntakeStep.invited,
                        icon: "positive",
                      }
                    : {
                        label: getLocalizedText("application.action.visit"),
                        targetStep: EIntakeStep.visitor,
                        icon: "positive",
                      },
                );
                actions.push({
                  label: getLocalizedText("application.action.reject"),
                  targetStep: EIntakeStep.notEligible,
                  icon: "negative",
                });
                break;

              case EIntakeStep.planAsVisitor:
                actions.push(
                  hasCalendarSync
                    ? {
                        label: getLocalizedText("application.action.invite"),
                        targetStep: EIntakeStep.invited,
                        icon: "positive",
                      }
                    : {
                        label: getLocalizedText("application.action.visit"),
                        targetStep: EIntakeStep.visitor,
                        icon: "positive",
                      },
                );
                actions.push({
                  label: getLocalizedText("application.action.reject"),
                  targetStep: EIntakeStep.notEligible,
                  icon: "negative",
                });
                break;

              case EIntakeStep.invited:
                actions.push({
                  label: getLocalizedText("application.action.visit_no_check"),
                  targetStep: EIntakeStep.visitor,
                  icon: "positive",
                });
                actions.push({
                  label: getLocalizedText("application.action.reject"),
                  targetStep: EIntakeStep.notEligible,
                  icon: "negative",
                });
                break;

              case EIntakeStep.visitor:
                actions.push({
                  label: getLocalizedText("application.action.candidate"),
                  targetStep: EIntakeStep.candidateTenant,
                  icon: "positive",
                });
                actions.push({
                  label: item.noShowAt
                    ? getLocalizedText("application.action.remove_no_show")
                    : getLocalizedText("application.action.no_show"),
                  icon: "negative",
                  customAction: () =>
                    dispatch(
                      applicationActions.setApplicationNoShowStart.getAction({
                        id: item.id,
                        value: !item.noShowAt,
                      }),
                    ),
                });
                break;

              case EIntakeStep.candidateTenant:
                actions.push({
                  label: getLocalizedText("application.action.tenant"),
                  targetStep: EIntakeStep.tenant,
                  icon: "positive",
                });
                actions.push({
                  label: getLocalizedText("application.action.reject"),
                  targetStep: EIntakeStep.notEligible,
                  icon: "negative",
                });
                break;

              case EIntakeStep.tenant:
                break;

              case EIntakeStep.notEligible:
                // Archive is handled differently — typically archiving the application
                break;
            }

            return actions.map((action, idx) => (
              <S.ActionButton
                key={idx}
                onClick={e => {
                  e.stopPropagation();
                  if (action.customAction) {
                    action.customAction();
                  } else if (action.targetStep) {
                    handler([item.id], action.targetStep);
                  }
                }}
              >
                {action.icon === "positive" && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
                    <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                  </svg>
                )}
                {action.icon === "negative" && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
                    <path d="M17 2h4a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4" />
                  </svg>
                )}
                {action.icon === "hourglass" && (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 22h14" />
                    <path d="M5 2h14" />
                    <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
                    <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                  </svg>
                )}
                {action.label}
              </S.ActionButton>
            ));
          })()}

          {/* Chevron / Dropdown menu */}
          <S.ChevronMenuWrapper onClick={(e: any) => e.stopPropagation()}>
            <DropdownMenu
              onOpen={checkAvailability}
              children={
                <S.ChevronButton>
                  <svg
                    width="8"
                    height="12"
                    viewBox="0 0 8 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="1 1 7 6 1 11" />
                  </svg>
                </S.ChevronButton>
              }
              actions={getRowActions()}
            />
          </S.ChevronMenuWrapper>
        </S.ActionsCell>
      </>
    );
  };

  if (!renderAsDragItem) {
    return (
      <>
        <S.TableRow
          hasSeparator={!isLast}
          onClick={() => {
            setQueryParamValue({ applicationId: item.id });
          }}
        >
          {renderRowContent()}
        </S.TableRow>
        {showDuplicateModal && (
          <MultiStepForm
            formId={`duplicate-application-${formId}`}
            schemas={createDuplicateApplicationForm({ broker, propertyId })}
            asModal={true}
            withAside={false}
            onSuccess={handleDuplicate}
            modalProps={{ onClose: handleCloseModal, width: "small" }}
          />
        )}
        {showOtherAppsModal && (
          <OtherApplicationsModal
            candidateName={getName(contacts[0] || {})}
            properties={item.otherApplicationsMeta?.properties ?? []}
            notes={item.otherApplicationsMeta?.notes ?? []}
            onClose={() => setShowOtherAppsModal(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Draggable draggableId={item.id} index={index} key={item.id}>
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
          if (
            isGlobalDragging &&
            selectedItemIds.includes(item.id) &&
            !snapshot.isDragging
          ) {
            return (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{ ...provided.draggableProps.style, display: "none" }}
              />
            );
          }

          if (snapshot.isDragging) {
            // Destructure width/height to prevent the measured row width from
            // causing horizontal overflow in the scrollable Table container.
            const {
              width: _w,
              height: _h,
              ...dragStyle
            } = (provided.draggableProps.style ?? {}) as any;
            // Render the drag clone in a portal on document.body so its
            // position:fixed styling is no longer scoped to the scrollable
            // Table container — otherwise the clone (offset by the grabbed
            // row's coordinates) expands the table's horizontal scroll area.
            return createPortal(
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{
                  ...dragStyle,
                  display: "flex",
                  alignItems: "center",
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                  padding: 0,
                  width: "auto",
                  height: "auto",
                  zIndex: 9999,
                }}
              >
                <div style={{ marginLeft: Math.max(0, clickX - 90) + "px" }}>
                  <S.DragPreviewPill>
                    <div
                      style={{
                        marginRight: "6px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    {selectedItemIds.length > 1
                      ? getLocalizedText(
                          "rentmatch.applications.drag_count_selected",
                          { count: String(selectedItemIds.length) },
                        )
                      : getLocalizedText(
                          "rentmatch.applications.drag_single_candidate",
                        )}
                  </S.DragPreviewPill>
                </div>
              </div>,
              document.body,
            );
          }

          return (
            <S.TableRow
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              isDragging={snapshot.isDragging}
              hasSeparator={!isLast}
              onClick={() => {
                setQueryParamValue({ applicationId: item.id });
              }}
              onMouseDown={(e: any) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setClickX(e.clientX - rect.left);
              }}
            >
              {renderRowContent()}
            </S.TableRow>
          );
        }}
      </Draggable>

      {showDuplicateModal && (
        <MultiStepForm
          formId={`duplicate-application-${formId}`}
          schemas={createDuplicateApplicationForm({ broker, propertyId })}
          asModal={true}
          withAside={false}
          onSuccess={handleDuplicate as any}
          modalProps={{ onClose: handleCloseModal, width: "small" }}
        />
      )}
      {showOtherAppsModal && (
        <OtherApplicationsModal
          candidateName={getName(contacts[0] || {})}
          properties={item.otherApplicationsMeta?.properties ?? []}
          notes={item.otherApplicationsMeta?.notes ?? []}
          onClose={() => setShowOtherAppsModal(false)}
        />
      )}
    </>
  );
});
